import { CertStatus, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|CertStatus`;

type SharedKeys = Pick<
	LocDef,
	'type' | 'portal' | 'displayType' | 'htmlAllowed' | 'portal' | 'shared' | 'feature'
>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.Credential,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type CPCertStatusLocs =
	| 'CPCertStatusEnumActive'
	| 'CPCertStatusEnumDeleted'
	| 'CPCertStatusEnumExpired'
	| 'CPCertStatusEnumStarted'
	| 'CPCertStatusEnumSuspended'
	| 'CPCertStatusEnumEmeritus'
	| 'CPCertStatusEnumLifetimeEmeritus';

export type SPCertStatusLocs =
	| 'SPCertStatusEnumActive'
	| 'SPCertStatusEnumDeleted'
	| 'SPCertStatusEnumExpired'
	| 'SPCertStatusEnumStarted'
	| 'SPCertStatusEnumSuspended'
	| 'SPCertStatusEnumEmeritus'
	| 'SPCertStatusEnumLifetimeEmeritus';

export const cpCertStatusEnumLocs: LocDefs<CPCertStatusLocs> = {
	CPCertStatusEnumActive: {
		defaultValue: 'Active',
		displayPath: `${dpRoot}|${CertStatus.Active}`,
		description: `System value for '${CertStatus.Active}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Active',
		...sharedCpLocData,
	},
	CPCertStatusEnumDeleted: {
		defaultValue: 'Deleted',
		displayPath: `${dpRoot}|${CertStatus.Deleted}`,
		description: `System value for '${CertStatus.Deleted}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Deleted',
		...sharedCpLocData,
	},
	CPCertStatusEnumSuspended: {
		defaultValue: 'Suspended',
		displayPath: `${dpRoot}|${CertStatus.Suspended}`,
		description: `System value for '${CertStatus.Suspended}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Suspended',
		...sharedCpLocData,
	},
	CPCertStatusEnumExpired: {
		defaultValue: 'Expired',
		displayPath: `${dpRoot}|${CertStatus.Expired}`,
		description: `System value for '${CertStatus.Expired}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Expired',
		...sharedCpLocData,
	},
	CPCertStatusEnumStarted: {
		defaultValue: 'In Progress',
		displayPath: `${dpRoot}|${CertStatus.Started}`,
		description: `System value for '${CertStatus.Started}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Started',
		...sharedCpLocData,
	},
	CPCertStatusEnumEmeritus: {
		defaultValue: 'Emeritus',
		displayPath: `${dpRoot}|${CertStatus.Emeritus}`,
		description: `System value for '${CertStatus.Emeritus}' credential status`,
		...sharedCpLocData,
	},
	CPCertStatusEnumLifetimeEmeritus: {
		defaultValue: 'Lifetime Emeritus',
		displayPath: `${dpRoot}|${CertStatus.LifetimeEmeritus}`,
		description: `System value for '${CertStatus.LifetimeEmeritus}' credential status`,
		...sharedCpLocData,
	},
};

export const spCertStatusEnumLocs: LocDefs<SPCertStatusLocs> = {
	SPCertStatusEnumActive: {
		defaultValue: 'Active',
		displayPath: `${dpRoot}|${CertStatus.Active}`,
		description: `System value for '${CertStatus.Active}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Active',
		...sharedSpLocData,
	},
	SPCertStatusEnumDeleted: {
		defaultValue: 'Deleted',
		displayPath: `${dpRoot}|${CertStatus.Deleted}`,
		description: `System value for '${CertStatus.Deleted}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Deleted',
		...sharedSpLocData,
	},
	SPCertStatusEnumSuspended: {
		defaultValue: 'Suspended',
		displayPath: `${dpRoot}|${CertStatus.Suspended}`,
		description: `System value for '${CertStatus.Suspended}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Suspended',
		...sharedSpLocData,
	},
	SPCertStatusEnumExpired: {
		defaultValue: 'Expired',
		displayPath: `${dpRoot}|${CertStatus.Expired}`,
		description: `System value for '${CertStatus.Expired}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Expired',
		...sharedSpLocData,
	},
	SPCertStatusEnumStarted: {
		defaultValue: 'In Progress',
		displayPath: `${dpRoot}|${CertStatus.Started}`,
		description: `System value for '${CertStatus.Started}' credential status`,
		legacyName: 'Credential/System.Enum.CertStatus.Started',
		...sharedSpLocData,
	},
	SPCertStatusEnumEmeritus: {
		defaultValue: 'Emeritus',
		displayPath: `${dpRoot}|${CertStatus.Emeritus}`,
		description: `System value for '${CertStatus.Emeritus}' credential status`,
		...sharedSpLocData,
	},
	SPCertStatusEnumLifetimeEmeritus: {
		defaultValue: 'Lifetime Emeritus',
		displayPath: `${dpRoot}|${CertStatus.LifetimeEmeritus}`,
		description: `System value for '${CertStatus.LifetimeEmeritus}' credential status`,
		...sharedSpLocData,
	},
};

export const CERT_STATUS_ENUM_LOCS = MapLocKeys<CPCertStatusLocs | SPCertStatusLocs>({
	...cpCertStatusEnumLocs,
	...spCertStatusEnumLocs,
});

export type CPCertStatusEnumLocType = LocMap<CPCertStatusLocs>;
export type SPCertStatusEnumLocType = LocMap<SPCertStatusLocs>;

export const cpCertStatusKeys: { [key in CertStatus]: keyof CPCertStatusEnumLocType } = {
	Active: CERT_STATUS_ENUM_LOCS.CPCertStatusEnumActive,
	Deleted: CERT_STATUS_ENUM_LOCS.CPCertStatusEnumDeleted,
	Suspended: CERT_STATUS_ENUM_LOCS.CPCertStatusEnumSuspended,
	Expired: CERT_STATUS_ENUM_LOCS.CPCertStatusEnumExpired,
	Started: CERT_STATUS_ENUM_LOCS.CPCertStatusEnumStarted,
	Emeritus: CERT_STATUS_ENUM_LOCS.CPCertStatusEnumEmeritus,
	LifetimeEmeritus: CERT_STATUS_ENUM_LOCS.CPCertStatusEnumLifetimeEmeritus,
};

export const spCertStatusKeys: { [key in CertStatus]: keyof SPCertStatusEnumLocType } = {
	Active: CERT_STATUS_ENUM_LOCS.SPCertStatusEnumActive,
	Deleted: CERT_STATUS_ENUM_LOCS.SPCertStatusEnumDeleted,
	Suspended: CERT_STATUS_ENUM_LOCS.SPCertStatusEnumSuspended,
	Expired: CERT_STATUS_ENUM_LOCS.SPCertStatusEnumExpired,
	Started: CERT_STATUS_ENUM_LOCS.SPCertStatusEnumStarted,
	Emeritus: CERT_STATUS_ENUM_LOCS.SPCertStatusEnumEmeritus,
	LifetimeEmeritus: CERT_STATUS_ENUM_LOCS.SPCertStatusEnumLifetimeEmeritus,
};
